import type { ComponentProps } from "react"
import { format } from "date-fns"
import cn from "@/v2-ui/utils/utils.cn"
import {
  activityLevel,
  weekGridRows,
} from "@/v2-backoffice-client/usage/usage.utils"

export type UsageWeekGridProps = ComponentProps<"div"> & {
  /** One value per day, oldest first, the last one today. */
  series: number[]
  /** Noun for the tooltip ("people"). */
  unit: string
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

// A month of days laid out as a calendar: one row per week, Monday to
// Sunday, each cell shaded by its value (the shared activity ramp, so the
// number stays legible on every shade) and carrying the number. The
// weekday columns are the point — a weekend that is empty, a Monday that
// is always the peak — which a flat strip of 28 cells cannot show.
function UsageWeekGrid(props: UsageWeekGridProps) {
  const { series, unit, className, ...divProps } = props
  const rows = weekGridRows(series)
  const max = Math.max(1, ...series)

  return (
    <div
      className={cn("usage-week-grid", className)}
      role="img"
      aria-label={`${series.length} days of ${unit} per day, by weekday`}
      {...divProps}
    >
      <span />
      {WEEKDAYS.map((day) => (
        <span key={day} className="usage-week-grid-head">
          {day}
        </span>
      ))}
      {rows.map((row) => (
        <div key={row.weekStart} className="contents">
          <span className="usage-week-grid-label">
            {format(new Date(row.weekStart), "d MMM")}
          </span>
          {row.days.map((day, index) =>
            day == null ? (
              <span key={index} />
            ) : (
              <span
                key={index}
                className={cn(
                  "usage-week-grid-cell",
                  `usage-week-grid-cell--${activityLevel(day.value, max)}`,
                )}
                title={`${format(new Date(day.date), "EEE d MMM")}: ${
                  day.value > 0 ? `${day.value} ${unit}` : `no ${unit}`
                }`}
              >
                {day.value > 0 ? day.value : ""}
              </span>
            ),
          )}
        </div>
      ))}
    </div>
  )
}

export default UsageWeekGrid
